import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { isCompletoTier } from "../utils/planFeatures";
import { PageHeaderBar, PageHeaderTitle, PageHeaderDescription } from "../components/PageHeader";
import RestaurantNotifications from "../components/notifications/RestaurantNotifications";
import NotificationLeadTime from "../components/notifications/NotificationLeadTime";
import { CustomModal } from "../components/CustomModal";
import {
  fetchRestaurantOpenDays,
  updateRestaurant,
  fetchHoursEach,
  fetchPlaceOfTable,
} from "../actions/restaurantActions";
import {
  Container,
  ModernButton,
} from "../components/DefaultImports";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { LuUtensils, LuUsers, LuBellRing, LuCalendarX2, LuLock, LuCheck } from "react-icons/lu";
import { OverviewButton } from "../components/overview/overviewStyles";

function Notifications() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [restaurant_id, setRestaurant_id] = useState(false);
  const [eachTime, setEachTime] = useState("00:15");
  const [acceptPeoplePerEachTime, setAcceptPeoplePerEachTime] = useState("");
  const [limitPeopleReservation, setLimitPeopleReservation] = useState("");
  const [errorMessage, setErrorMessage] = useState("ok_status");
  const [showError, setShowError] = useState(false);
  const [activeTab, setActiveTab] = useState("restaurant_notifications");
  const [reconfirmation_duration, setReconfirmation_duration] = useState("");
  const [reconfirmation_time_before_date, setReconfirmation_time_before_date] =
    useState("");
  const [messageReminderTime, setMessageReminderTime] = useState("");
  const [timeToCancelledWarning, setTimeToCancelledWarning] = useState("");
  const [reconfirmationCompareError, setReconfirmationCompareError] =
    useState("");
  const [didTrySave, setDidTrySave] = useState(false);
  const [touchedBeforeDate, setTouchedBeforeDate] = useState(false);
  const [showReminderUpgradeModal, setShowReminderUpgradeModal] =
    useState(false);

  const restaurantDetails = useSelector((state) => state.restaurantDetails);
  const { restaurants } = restaurantDetails;

  const isReminderAndReconfirmationBlocked =
    !restaurants || !isCompletoTier(restaurants?.sub_lvl);

  useEffect(() => {
    if (restaurants) {
      setRestaurant_id(restaurants.id);
      dispatch(fetchPlaceOfTable(restaurants.id));
    }
  }, [restaurants, dispatch]);

  useEffect(() => {
    if (!restaurant_id) return;
    dispatch(fetchRestaurantOpenDays(restaurant_id));
    dispatch(fetchHoursEach(restaurant_id));
  }, [dispatch, restaurant_id]);

  useEffect(() => {
    if (!restaurants) return;

    if (restaurants.each_time) {
      setEachTime(restaurants.each_time);
    }
    if (restaurants.accept_people_per_each_time !== undefined) {
      setAcceptPeoplePerEachTime(restaurants.accept_people_per_each_time);
    }
    if (restaurants.limit_people_reservation !== undefined) {
      setLimitPeopleReservation(restaurants.limit_people_reservation);
    }

    setReconfirmation_duration(
      restaurants?.reconfirmation_duration != null
        ? String(restaurants.reconfirmation_duration)
        : "",
    );
    setReconfirmation_time_before_date(
      restaurants?.reconfirmation_time_before_date != null
        ? String(restaurants.reconfirmation_time_before_date)
        : "",
    );
    setMessageReminderTime(restaurants.send_message_reminder_to_customers);
    setTimeToCancelledWarning(restaurants.time_to_cancelled_warning);
  }, [restaurants]);

  const toIntOrNull = (value) => {
    const cleanValue = String(value ?? "").trim();
    if (cleanValue === "") return null;

    const parsedValue = parseInt(cleanValue, 10);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  };

  useEffect(() => {
    const durationNumber = toIntOrNull(reconfirmation_duration);
    const beforeNumber = toIntOrNull(reconfirmation_time_before_date);

    if (
      durationNumber != null &&
      beforeNumber != null &&
      beforeNumber > durationNumber
    ) {
      setReconfirmationCompareError(
        t("ui.i18nMessages.reconfirmationRangeError"),
      );
    } else {
      setReconfirmationCompareError("");
    }
  }, [reconfirmation_duration, reconfirmation_time_before_date, t]);

  const handleReconfirmation = (value) => {
    if (isReminderAndReconfirmationBlocked) return;
    setReconfirmation_duration(value);
  };

  const handleReconfirmationTimeBeforeDate = (value) => {
    if (isReminderAndReconfirmationBlocked) return;
    setTouchedBeforeDate(true);
    setReconfirmation_time_before_date(value);
  };

  const handleMessageReminderTime = (value) => {
    if (isReminderAndReconfirmationBlocked) return;
    setMessageReminderTime(value);
  };

  const handleTimeCancelWarning = (value) => {
    setTimeToCancelledWarning(value);
  };

  function checkEmptyStates() {
    const emptyStates = [];

    if (!eachTime) {
      emptyStates.push(t("ui.i18nFinalCleanup.timeInterval"));
    }
    if (!acceptPeoplePerEachTime || acceptPeoplePerEachTime <= 0) {
      emptyStates.push(t("ui.i18nFinalCleanup.capacityPerSlot"));
    }
    if (!limitPeopleReservation || limitPeopleReservation <= 0) {
      emptyStates.push(t("ui.i18nFinalCleanup.groupLimit"));
    }

    return emptyStates.length > 0
      ? t("ui.i18nFinalCleanup.fillFields", {
          fields: emptyStates.join(", "),
        })
      : "ok_status";
  }

  function checkForErrorsAndUpdateRestaurant() {
    const statusError = checkEmptyStates();
    setErrorMessage(statusError);

    if (statusError !== "ok_status") {
      setShowError(true);
      return;
    }

    setShowError(false);
    dispatch(
      updateRestaurant(restaurant_id, {
        time_to_cancelled_warning: timeToCancelledWarning,
        send_message_reminder_to_customers:
          isReminderAndReconfirmationBlocked
            ? restaurants?.send_message_reminder_to_customers
            : messageReminderTime,
        reconfirmation_time_before_date: isReminderAndReconfirmationBlocked
          ? restaurants?.reconfirmation_time_before_date
          : reconfirmation_time_before_date,
        reconfirmation_duration: isReminderAndReconfirmationBlocked
          ? restaurants?.reconfirmation_duration
          : reconfirmation_duration,
      }),
    );

    toast.success(t("ui.i18nMessages.savedSuccessfully"), {
      style: { color: "#EE754C" },
      icon: (
        <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#EE754C" }} />
      ),
      progressStyle: { backgroundColor: "#EE754C" },
      autoClose: 1000,
    });
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    setDidTrySave(true);
    checkForErrorsAndUpdateRestaurant();
  };

  return (
    <NotificationsPage>
      <ToastContainer />

      <PageHeader>
        <PageTitle>
          {t("ui.screens.notifications.notificacoes")}
        </PageTitle>

      <TabsShell data-setup-tour="notifications">
        <StyledTabButton
          type="button"
          $active={activeTab === "restaurant_notifications"}
          aria-pressed={activeTab === "restaurant_notifications"}
          onClick={() => setActiveTab("restaurant_notifications")}
        >
          <LuUtensils aria-hidden="true" />
          {t("ui.screens.notifications.restaurante")}
        </StyledTabButton>

        <StyledTabButton
          type="button"
          $active={activeTab === "client_notifications"}
          aria-pressed={activeTab === "client_notifications"}
          onClick={() => setActiveTab("client_notifications")}
        >
          <LuUsers aria-hidden="true" />
          {t("ui.screens.notifications.clientes")}
        </StyledTabButton>
      </TabsShell>
      </PageHeader>
      <PageIntro>{t(activeTab === "restaurant_notifications" ? "notificationsDesign.restaurantIntro" : "notificationsDesign.customerIntro")}</PageIntro>

      <TabContent>
        {activeTab === "restaurant_notifications" && (
          <RestaurantNotifications restaurant_id={restaurant_id} />
        )}

        {activeTab === "client_notifications" && (
          <Form onSubmit={handleSubmit}>
            <SettingsGrid>
              <SettingSection>
                <SettingHeading>
                  <LuBellRing aria-hidden="true" />
                  {t("ui.screens.notifications.lembretes")}
                </SettingHeading>

                  <FieldRow>
                    <span>
                      {t(
                        "ui.screens.notifications.enviarLembreteDaReservaAoCliente",
                      )}
                    </span>

                    {isReminderAndReconfirmationBlocked ? (
                      <PlanButton type="button" onClick={() => setShowReminderUpgradeModal(true)} aria-label={t("ui.screens.notifications.verPlanosParaLembretes")}>
                        <LuLock aria-hidden="true" />{t("ui.screens.notifications.completo")}
                      </PlanButton>
                    ) : (
                      <NotificationLeadTime
                        label={t("ui.screens.notifications.lembretes")}
                        initialValue={
                          restaurants?.send_message_reminder_to_customers
                        }
                        setValue={handleMessageReminderTime}
                      />
                    )}

                    <span>
                      {t("ui.screens.notifications.antesDaHoraDaReserva")}
                    </span>
                  </FieldRow>
              </SettingSection>

              <SettingSection>
                <SettingHeading>
                  <LuCalendarX2 aria-hidden="true" />
                  {t("ui.screens.notifications.notificacoes2")}
                </SettingHeading>

                  <FieldRow>
                    <span>
                      {t(
                        "ui.screens.notifications.receberNotificacaoSeOClienteCancelar",
                      )}
                    </span>

                    <NotificationLeadTime
                      label={t("ui.screens.notifications.notificacoes2")}
                      initialValue={restaurants?.time_to_cancelled_warning}
                      setValue={handleTimeCancelWarning}
                    />

                    <span>
                      {t("ui.screens.notifications.deAntecedencia")}
                    </span>
                  </FieldRow>
              </SettingSection>
            </SettingsGrid>

            {showError && errorMessage !== "ok_status" && (
              <InlineError>{errorMessage}</InlineError>
            )}

            {(didTrySave || touchedBeforeDate) &&
              reconfirmationCompareError && (
                <InlineError>{reconfirmationCompareError}</InlineError>
              )}

            <ButtonsContainer>
              <OverviewButton type="submit" $primary>
                <LuCheck aria-hidden="true" />
                {t("ui.screens.notifications.guardar")}
              </OverviewButton>
            </ButtonsContainer>
          </Form>
        )}
      </TabContent>

      <CustomModal
        show={showReminderUpgradeModal}
        onHide={() => setShowReminderUpgradeModal(false)}
        title={t("ui.screens.notifications.lembretesEReconfirmacoes")}
        body={
          <UpgradeModalText>
            {t(
              "ui.screens.notifications.osLembretesEPedidosDeReconfirmacao",
            )}{" "}
            <UpgradeModalHighlight>
              {t("ui.screens.notifications.completo")}
            </UpgradeModalHighlight>
            .
          </UpgradeModalText>
        }
        footer={
          <>
            <ModernButton
              type="button"
              color="#99b0ad"
              onClick={() => setShowReminderUpgradeModal(false)}
            >
              {t("ui.screens.notifications.agoraNao")}
            </ModernButton>

            <ModernButton
              type="button"
              color="#EE754C"
              onClick={() => {
                setShowReminderUpgradeModal(false);
                navigate(`/pick_plan_and_pay/${restaurants?.id}`);
              }}
            >
              {t("ui.screens.notifications.verPlanos")}
            </ModernButton>
          </>
        }
      />
    </NotificationsPage>
  );
}

export default Notifications;

const NotificationsPage = styled(Container)`
  padding: 12px 16px 28px;
  color: #344b43;
  @media (max-width: 600px) { padding: 8px 10px 20px; }
`;
const PageHeader = styled(PageHeaderBar)`
  @media (max-width: 540px) { flex-wrap: wrap; gap: 10px; }
`;
const PageTitle = PageHeaderTitle;
const PageIntro = styled(PageHeaderDescription)`
  margin: 20px 0;
  @media (max-width: 600px) { margin: 15px 0; }
`;
const SettingsGrid = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 32px; border: 1px solid #e5ece7; border-radius: 12px; padding: 24px;
  > section + section { border-left: 1px solid #e7eeea; padding-left: 32px; }
  @media (max-width: 850px) {
    grid-template-columns: minmax(0, 1fr); padding: 18px; gap: 20px;
    > section + section { border-left: 0; border-top: 1px solid #e7eeea; padding: 20px 0 0; }
  }
`;
const SettingSection = styled.section`min-width: 0;`;
const SettingHeading = styled.h2`
  display: flex; align-items: center; gap: 10px; margin: 0 0 18px;
  color: #3e564b; font-size: 16px; font-weight: 650;
  svg { width: 19px; height: 19px; color: #7b998a; flex-shrink: 0; stroke-width: 1.75; }
`;
const PlanButton = styled(OverviewButton)`
  background: #fff8f2; border-color: #f2dfd0; color: #b76a43; min-height: 40px;
`;
const TabsShell = styled.div`
  display: inline-flex;
  gap: 3px;
  padding: 4px;
  background: #f4f7f6;
  border-radius: 10px;

  @media (max-width: 540px) {
    display: flex;
    width: 100%;
  }
`;

const StyledTabButton = styled.button`
  min-height: 34px;
  padding: 7px 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 0;
  border-radius: 8px;
  background: ${({ $active }) => ($active ? "#ffffff" : "transparent")};
  color: ${({ $active }) => ($active ? "#ee754c" : "#687875")};
  box-shadow: ${({ $active }) => $active ? "0 1px 3px rgba(43,66,57,.06)" : "none"};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
  svg { width: 16px; height: 16px; flex-shrink: 0; stroke-width: 1.75; }

  &:hover {
    color: #ee754c;
    background: #ffffff;
  }

  @media (max-width: 540px) {
    flex: 1 1 0;
  }
`;

const TabContent = styled.div`
  width: 100%;
`;

const Form = styled.form`
  width: 100%;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  font-size: 13px;
  line-height: 1.6;
  color: #70817a;
`;

const UpgradeModalText = styled.p`
  margin: 0;
`;

const UpgradeModalHighlight = styled.span`
  color: #ee754c;
  font-weight: 700;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

const InlineError = styled.div`
  margin-top: 8px;
  color: #dc3545;
  font-size: 12px;
`;
